import { AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit, ViewChild, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ModalController, NotifierController, PopupWindow, CommandService, StateService, Random } from 'flux-core';
import { DiagramLocatorLocator } from '../../diagram/locator/diagram-locator-locator';
import { BaseDiagramCommandEvent } from '../../diagram/command/base-diagram-command-event';
import { DiagramCommandEvent } from '../../../editor/diagram/command/diagram-command-event';
import { Subject, Subscription, timer } from 'rxjs';
import { PresentationHelpers } from '../presentation-helpers';
import { PresentingMode } from '../model/presentation.mdl';
import { SlideModel } from '../model/slide.mdl';
import { PresentingService } from '../presenting.svc';
import { PresentationLocator } from '../presentation-locator.svc';
import { concatMap, switchMap, tap, take, last } from 'rxjs/operators';

/**
 * This dialog is for selecting a new presentation to add slide to,
 * or creating a new presentation.
 */
@Component({
    templateUrl: './select-presentation-window.cmp.html',
    selector: 'select-presentation-window',
    styleUrls: [ './select-presentation-window.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectPresentationWindow extends PopupWindow implements OnInit, AfterViewInit, OnDestroy {
    public closeWindowObs: Subject<boolean> = new Subject();

    @Input()
    public presentations: any[];

    @Input()
    public shapeId: string;

    /**
     * The window overlay.
     */
    @ViewChild( 'window' ) protected container;

    /**
     * The the window element.
     */
    @ViewChild( 'windowInner' ) protected containerInner;

    public selectionData = [];
    public selectedId: string = '';
    public option: string = 'existing';
    public presentationName: string;
    protected subs: Subscription[] = [];

    constructor(
        protected modalController: ModalController,
        protected commandService: CommandService,
        protected state: StateService<any, any>,
        protected translate: TranslateService,
        protected notifierController: NotifierController,
        protected dll: DiagramLocatorLocator,
        protected presentingService: PresentingService,
        protected pl: PresentationLocator,
    ) {
        super()/* istanbul ignore next */;
    }

    public ngOnInit(): void {
        this.subs.push( this.closeWindowObs.subscribe(() => this.closeWindow()));
        this.selectionData = this.presentations.map( i => ({ ...i, label: i.name, title: i.name, buttonLabel: i.name, selected: false }));
        this.presentationName = 'Presentation ' + PresentationHelpers.generateName( this.presentations.length + 1 );
        if ( this.selectionData.length === 0 ) {
            this.option = 'create';
        }
    }

    public ngAfterViewInit() {
    }

    /**
     * Closes the window after animation is complete.
     */
    public closeWindow() {
        this.hideWindow( this.container, this.containerInner ).subscribe({
            complete: () => {
                this.modalController.hide();
            },
        });
    }

    public submitForm() {
        if ( this.option === 'create' ) {
            this.createNewPresentation();
        } else if ( this.selectedId ) {
            this.addSlideToPresentation();
        }
    }

    public ngOnDestroy() {
        while ( this.subs.length > 0 ) {
            this.subs.pop().unsubscribe();
        }
    }

    public setOption( val ) {
        this.option = val;
    }

    public updatePresentationName(event: Event) {
        const inputElement = event.target as HTMLInputElement;
        this.presentationName = inputElement.value;
    }

    protected selectPresentation ( event ) {
        this.option = 'existing';
        this.selectedId = event[0];
        this.selectionData = this.selectionData
            .map( o => ({ ...o, selected: o.id === this.selectedId }));
    }

    protected addSlideToPresentation() {
        this.pl.getPresentation( this.selectedId, this.state.get( 'CurrentUser' )).pipe(
            take( 1 ),
            switchMap( presentation => {
                const slideId = Random.slideId();
                const diagramId = this.state.get( 'CurrentDiagram' );
                const loadingText = this.translate.instant( 'SLIDES_PANEL.PRESENTATION_CREATED_AND_SLIDE_ADDED' )

                const slide = {
                    id: slideId,
                    presentationId: this.selectedId,
                    diagramId: diagramId,
                    title: 'Slide ' + PresentationHelpers.generateName( presentation.slides ? Object.keys( presentation.slides ).length + 1 : 1 ),
                    shapes: [this.shapeId],
                    mode: PresentingMode.FOCUSED,
                    slideShapeId: this.shapeId,
                } as SlideModel;
                                    
                if ( presentation.slides ) {
                    presentation.slides[ slideId ] = slide;
                } else {
                    presentation.slides = { [slideId]: slide };
                }
                                    
                return this.commandService.dispatch( DiagramCommandEvent.updatePresentation, {
                    presentation: { $set: presentation },
                    presentationId: presentation.id,
                }).pipe(
                    last(),
                    tap(() => {
                        this.closeWindow();
                        this.state.set( 'CurrentPresentation', this.selectedId )
                        this.state.set( 'AcknowledgementMessage', { open: true, message: loadingText, showLoader: false });

                        this.state.set( 'LeftSidebarVisibility', true );
                        const current = this.state.get( 'InitializedLeftSizebarPanelIds' ) as Set<string>;
                        current.add( 'slides' );
                        this.state.set( 'InitializedLeftSizebarPanelIds', current );
                        setTimeout(() => this.state.set( 'SelectedLeftPanel', 'slides' ), 0 );
                    }),
                    concatMap(() => timer(2000)), // Wait for 2 seconds instead of a negligible delay
                    tap(() => this.state.set( 'AcknowledgementMessage', { open: false, message: '', showLoader: false })),
                    switchMap(() => this.commandService.dispatch( DiagramCommandEvent.updateDataItems, {
                        shapeIds: [ this.shapeId ],
                        dataItems: [ { id: PresentingService.PRESENTATION_DATA_KEY, value: presentation.id }, { id: PresentingService.SLIDE_DATA_KEY, value: slideId } ]
                    })),
                    switchMap(() => this.presentingService.updateShapeTextToSlide(
                        this.shapeId,
                        diagramId,
                        'Slide ' + PresentationHelpers.generateName( Object.keys( presentation.slides ).length ),
                        'slideTitle'
                    )),
                    tap(() => {
                        this.state.set( 'CurrentPresentation', presentation.id );
                        this.state.set( 'ActiveSlide', slideId );
                        this.presentingService.scrollToSlide( slideId );
                    })
                )
            })
        ).subscribe();
    }

    protected createNewPresentation() {
        const userId = this.state.get( 'CurrentUser' );
        const projectId = this.state.get( 'CurrentProject' );
        const diagramId = this.state.get( 'CurrentDiagram' );
        let name = this.presentationName;
        const loadingText = this.translate.instant( 'SLIDES_PANEL.PRESENTATION_CREATED_AND_SLIDE_ADDED' )
        const presentationId = Random.presentationId()
        const slideId = Random.slideId();
    
        // this.subs.push(
            this.dll.forDiagram( diagramId, false ).getDiagramOnce().pipe(
                take( 1 ),
                switchMap( diagram => {
                    if (( diagram.project ) as any !== projectId ) {
                        this.presentingService.showErrorNotification();
                        return;
                    }
                    const slide = {
                        id: slideId,
                        presentationId: presentationId,
                        diagramId: diagramId,
                        title: 'Slide ' + PresentationHelpers.generateName( 1 ),
                        shapes: [this.shapeId],
                        mode: PresentingMode.FOCUSED,
                        slideShapeId: this.shapeId
                    } as SlideModel;

                    const presentation = {
                        id: presentationId,
                        projectId: projectId,
                        userId: userId,
                        name: name,
                        slides: { [slideId]: slide }
                    };
                    return this.commandService.dispatch( BaseDiagramCommandEvent.createPresentation, { presentation }).pipe(
                        last(),
                        tap(() => {
                            this.closeWindow();
                            this.state.set( 'CurrentPresentation', presentationId )
                            this.state.set( 'AcknowledgementMessage', { open: true, message: loadingText, showLoader: false });

                            this.state.set( 'LeftSidebarVisibility', true );
                            const current = this.state.get( 'InitializedLeftSizebarPanelIds' ) as Set<string>;
                            current.add( 'slides' );
                            this.state.set( 'InitializedLeftSizebarPanelIds', current );
                            setTimeout(() => this.state.set( 'SelectedLeftPanel', 'slides' ), 0 );
                        }),
                        concatMap(() => timer(2000)), // Wait for 2 seconds instead of a negligible delay
                        tap(() => this.state.set( 'AcknowledgementMessage', { open: false, message: '', showLoader: false })),
                        switchMap(() => this.commandService.dispatch( DiagramCommandEvent.updateDataItems, {
                            shapeIds: [ this.shapeId ],
                            dataItems: [ { id: PresentingService.PRESENTATION_DATA_KEY, value: presentation.id }, { id: PresentingService.SLIDE_DATA_KEY, value: slideId } ]
                        })),
                        switchMap(() => this.presentingService.updateShapeTextToSlide(
                            this.shapeId,
                            diagramId,
                            'Slide ' + PresentationHelpers.generateName( Object.keys( presentation.slides ).length || 1 ),
                            'slideTitle'
                        )),
                        tap(() => {
                            this.state.set( 'CurrentPresentation', presentationId );
                            this.state.set( 'ActiveSlide', slideId );
                            this.presentingService.scrollToSlide( slideId );
                        })
                    );
                }),
            ).subscribe();
    }

}
